import { useEffect } from 'react';
import { useDetermineMobile } from '@myrealtrip/device-detect';
import styled from '@emotion/styled';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useEmblaCarousel from 'embla-carousel-react';
import { globalVariables } from '../../../designSystem/constants';
import { WeblogPageCategory, WeblogScreenName } from '../../../constants/weblog';
import { AdsShrinkingBanner, CookieKey } from '@myrealtrip/ads';
import { useEnvContext } from '../../../../modules/EnvProvider';
import { fetchInteractiveBanner } from '../../../api/ads';
import CookieManager from '../../../utils/cookieManager';
import { logBrazeEvent } from '@myrealtrip/marketing-log';
import { helper } from '@myrealtrip/frontend-weblog';
import DomainManager from '../../../utils/DomainManager';
import { weblog } from '@myrealtrip/logger';
import SearchManager from '../SearchManager';
import { useLogData } from '../LogDataContext';
import { RecentKeywords } from '../RecentKeywords';

const { LoggingImpression } = weblog;
import { PromotionBanner } from '../PromotionBanner';
import useGetHotKeywords from '../hooks/apis/useGetHotKeywords';
import useGetRecentViewProducts from '../hooks/apis/useGetRecentViewProducts';
import RecentProductCard from './RecentProductCard';

const { sendSearchLog, sendImpressionLog } = helper;
const searchManager = new SearchManager();

const baseSearchLog = {
  eventName: 'gsearch',
  itemKind: 'hot_keyword',
  screenName: WeblogScreenName.COMMON,
  pageCategory: WeblogPageCategory.COMMON,
};

interface Props {
  handleRef?: (idx: number, node: HTMLAnchorElement) => void;
  selectedIdx?: number;
}

function InitialSearchView({ handleRef, selectedIdx }: Props) {
  const { targetEnv } = useEnvContext();
  const isMobile = useDetermineMobile();
  const logData = useLogData();
  const {
    keywords: hotKeywords,
    date: hotKeywordDate,
    isError: isHotKeywordsError,
    isLoading: isHotKeywordsLoading,
  } = useGetHotKeywords();
  const hasHotKeywords = !isHotKeywordsError && hotKeywords.length > 0;
  const showHotKeywordsColumn = isHotKeywordsLoading || hasHotKeywords;
  const { products, isLoading: isProductsLoading } = useGetRecentViewProducts();
  const [emblaRef] = useEmblaCarousel({ dragFree: true, containScroll: 'trimSnaps' });
  const dateString = hotKeywordDate ? `${hotKeywordDate.replace(/-/g, '.')} 기준` : '';
  const queryClient = useQueryClient();
  const domainManager = new DomainManager(targetEnv);
  const inventoryId = CookieManager.get(CookieKey.INTERACTIVE_BANNER_ID) || '';

  const { data: bannerData } = useQuery(
    ['fetchInteractiveBanner', { targetEnv, inventoryId }],
    () =>
      fetchInteractiveBanner(
        targetEnv,
        'search_home',
        'INTERACTIVE_BANNER_IMAGE_AD_CONTAINER_1',
        inventoryId,
      ),
    {
      enabled: isMobile && Boolean(inventoryId),
    },
  );

  const banner = bannerData?.data?.data?.inventories?.[0];

  const handleBannerClose = () => {
    queryClient.removeQueries({
      queryKey: ['fetchInteractiveBanner', { targetEnv, inventoryId }],
    });
  };

  // 검색 초기 화면 impression
  useEffect(() => {
    try {
      sendImpressionLog(
        {
          eventName: 'gsearch',
          screenName: logData?.screenName ?? WeblogScreenName.COMMON,
          pageCategory: logData?.pageCategory ?? WeblogPageCategory.COMMON,
          ...logData?.params,
        },
        'gsearch',
      );
    } catch (e) {
      // ignore
    }
  }, []);

  const getHotKeywordUrl = (keyword: string) =>
    `${domainManager.getTravelerServiceDomain()}/search-bridge?q=${encodeURIComponent(keyword)}`;

  const handleHotKeywordClick = (keyword: string, rank: number) => {
    searchManager.setRecentKeywords(keyword);

    try {
      logBrazeEvent('search_offer', { search_offer: keyword, source: 'hot_keyword', rank });
      sendSearchLog({
        ...baseSearchLog,
        searchWord: keyword,
        targetUrl: getHotKeywordUrl(keyword),
        rank,
        screenName: logData?.screenName ?? baseSearchLog.screenName,
        pageCategory: logData?.pageCategory ?? baseSearchLog.pageCategory,
        ...logData?.params,
      });
    } catch (e) {
      // ignore
    }
  };

  return (
    <Container>
      {/* 모바일 전용: 인터렉티브 배너 */}
      {isMobile && (
        <MobileInteractiveBanner>
          <AdsShrinkingBanner
            banner={banner}
            animationMode="FIX"
            initialStep="COMPACT"
            onBannerClose={handleBannerClose}
            logData={{
              screenName: 'search_home',
              mylink_id: '',
            }}
          />
        </MobileInteractiveBanner>
      )}

      <RightColumn>
        <BannerArea>
          <PromotionBanner />
        </BannerArea>
        {isMobile && !isProductsLoading && products.length === 0 ? null : (
          <CarouselArea>
            <CarouselHeader>
              <SectionTitle>최근 본 상품</SectionTitle>
            </CarouselHeader>
            {isProductsLoading ? (
              <EmptyMessage>최근 본 상품을 불러오는 중입니다.</EmptyMessage>
            ) : products.length > 0 ? (
              isMobile ? (
                <MobileScrollContainer>
                  {products.map((product, idx) => (
                    <RecentProductCard key={product.productUrl} product={product} rank={idx + 1} />
                  ))}
                </MobileScrollContainer>
              ) : (
                <EmblaViewport ref={emblaRef}>
                  <EmblaContainer>
                    {products.map((product, idx) => (
                      <EmblaSlide key={product.productUrl}>
                        <RecentProductCard product={product} rank={idx + 1} />
                      </EmblaSlide>
                    ))}
                  </EmblaContainer>
                </EmblaViewport>
              )
            ) : (
              <EmptyMessage>최근 본 상품이 없습니다.</EmptyMessage>
            )}
          </CarouselArea>
        )}
      </RightColumn>
      {showHotKeywordsColumn && !(isMobile && !isProductsLoading && products.length === 0) && (
        <Divider />
      )}
      {showHotKeywordsColumn && (
        <LeftColumn>
          {hasHotKeywords && (
            <>
              <SectionHeader>
                <SectionTitle>인기검색어</SectionTitle>
                <DateLabel>{dateString}</DateLabel>
              </SectionHeader>
              <KeywordList>
                {hotKeywords.map((item, idx) => (
                  <LoggingImpression
                    key={item.keyword}
                    screenName={logData?.screenName ?? WeblogScreenName.COMMON}
                    eventName="gsearch"
                    pageCategory={logData?.pageCategory ?? WeblogPageCategory.COMMON}
                    params={{
                      itemKind: 'text',
                      keyword: item.keyword,
                      columnName: '인기검색어',
                      columnCode: 'hot_keywords',
                      columnIndex: isMobile ? 2 : 0,
                      itemVerticalIndex: idx,
                      ...logData?.params,
                    }}
                    impressionOptions={{ key: `hot_keyword_${item.keyword}`, threshold: 0.5 }}
                  >
                    <KeywordItem
                      href={getHotKeywordUrl(item.keyword)}
                      onClick={() => handleHotKeywordClick(item.keyword, idx + 1)}
                    >
                      <Rank highlight={idx < 3}>{idx + 1}</Rank>
                      <KeywordLabel>{item.keyword}</KeywordLabel>
                    </KeywordItem>
                  </LoggingImpression>
                ))}
              </KeywordList>
            </>
          )}
        </LeftColumn>
      )}
      <CenterDivider />
      <RecentKeywordsColumn>
        <RecentKeywords handleRef={handleRef} selectedIdx={selectedIdx} showEmpty={!isMobile} />
      </RecentKeywordsColumn>
    </Container>
  );
}

export default InitialSearchView;

const Container = styled.div`
  display: flex;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LeftColumn = styled.div`
  width: 200px;
  flex-shrink: 0;
  padding: 20px 12px 8px;
  order: 1;

  @media (max-width: 768px) {
    width: auto;
    padding: 12px;
    order: 5;
  }
`;

const Divider = styled.div`
  width: 1px;
  background-color: ${globalVariables.gray_100};
  align-self: stretch;
  order: 2;

  @media (max-width: 768px) {
    width: 100%;
    height: 1px;
    order: 4;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  order: 5;

  @media (max-width: 768px) {
    order: 3;
  }
`;

const BannerArea = styled.div`
  flex-shrink: 0;
  padding-top: 20px;
  padding-left: 16px;
  padding-right: 16px;

  @media (max-width: 768px) {
    padding: 4px 0 0;
  }
`;

const CarouselArea = styled.div`
  padding: 12px 16px 16px;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const CarouselHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const MobileScrollContainer = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const EmblaViewport = styled.div`
  overflow: hidden;
`;

const EmblaContainer = styled.div`
  display: flex;
  gap: 12px;
`;

const EmblaSlide = styled.div`
  flex: 0 0 auto;
`;

const EmptyMessage = styled.p`
  font-size: 14px;
  color: ${globalVariables.gray_400};
  text-align: center;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 198px;

  @media (max-width: 768px) {
    min-height: 120px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const SectionTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${globalVariables.gray_800};
`;

const DateLabel = styled.span`
  font-size: 11px;
  color: ${globalVariables.gray_400};
`;

const KeywordList = styled.div`
  margin: 0;
  padding: 0;
`;

const KeywordItem = styled.a`
  display: flex;
  align-items: center;
  padding: 5px 0;
  cursor: pointer;
  text-decoration: none;
  color: inherit;

  &:hover {
    background-color: ${globalVariables.gray_50};
  }
`;

const Rank = styled.span<{ highlight: boolean }>`
  width: 24px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ highlight }) => (highlight ? globalVariables.blue_400 : globalVariables.gray_500)};
  margin-right: 8px;
  text-align: center;
  flex-shrink: 0;
  line-height: 20px;
`;

const KeywordLabel = styled.span`
  font-size: 15px;
  line-height: 20px;
  color: ${globalVariables.gray_700};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CenterDivider = styled.div`
  width: 1px;
  background-color: ${globalVariables.gray_100};
  align-self: stretch;
  order: 4;

  @media (max-width: 768px) {
    display: none;
  }
`;

const RecentKeywordsColumn = styled.div`
  width: 200px;
  flex-shrink: 0;
  padding: 0;
  order: 3;

  @media (max-width: 768px) {
    width: auto;
    padding: 0;
    order: 2;
  }
`;

const MobileInteractiveBanner = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    order: 1;
  }
`;
